import blockAdapter, { CustomHandle } from '@/adapter/blockAdapter';
import ConfirmPopover from '@/components/common/ConfirmPopover';
import AddBlockButton from '@/components/navigation/AddBlockButton';
import NewBlockDialog from '@/components/navigation/NewBlockDialog';
import SearchBlocksButton from '@/components/navigation/SearchBlocksButton';
import SearchBlocksDialog from '@/components/navigation/SearchBlocksDialog';
import { useApplication } from '@/hooks/useApplication';
import { useClipboard } from '@/hooks/useClipboard';
import { useDialogs } from '@/hooks/useDialogs';
import { useMetadata } from '@/hooks/useMetadata';
import { useSelected } from '@/hooks/useSelected';
import { useSubroutine } from '@/hooks/useSubroutine';
import { useThemeMode } from '@/hooks/useThemeMode';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { useViewportContext } from '@/hooks/useViewport';
import { Application } from '@/types/application';
import { ClipboardNode } from '@/types/clipboard';
import { Block } from '@/types/metadata/metadata';
import {
  ConnectionRef,
  addBlock,
  findSubroutine,
  getBlockHistory,
  redirectConnections,
  setOrReplaceAppStartblock,
  setOrReplaceStartblock,
  updateConnection,
  updatePositions
} from '@/util/application';
import { getClipboardData, handlePasteNodesIntoApplication, hasClipboardData } from '@/util/clipboard';
import { getColorFromString } from '@/util/color';
import { deleteBlock } from '@/util/delete';
import { dismissOnContextMenu } from '@/util/menu';
import { migrateApplication } from '@/util/migration/migration';
import { sleep } from '@/util/time';
import { isClipboardNodeData, isConnection, isNumber, isXYPosition } from '@/util/typeGuards';
import AddIcon from '@mui/icons-material/Add';
import AddBoxIcon from '@mui/icons-material/AddBox';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import StopTwoToneIcon from '@mui/icons-material/StopTwoTone';
import { Box, Divider, ListItemIcon, Menu, MenuItem, useTheme } from '@mui/material';
import {
  Background,
  BackgroundVariant,
  BezierEdge,
  Connection,
  ConnectionLineType,
  Controls,
  Edge,
  Node,
  ReactFlow,
  StepEdge,
  StraightEdge,
  Viewport,
  XYPosition,
  addEdge,
  useEdgesState,
  useNodesState,
  useOnViewportChange,
  useReactFlow,
  useUpdateNodeInternals
} from '@xyflow/react';
import { FC, MouseEvent, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import NodeRenderer from './NodeRenderer';
import createReconnectConnectionLine from './ReconnectConnectionLine';
import CustomNote from './nodes/CustomNote';
import SmartEdgeCached from './nodes/smartEdge/SmartEdgeCached';

type FlowMapProps = {
  onNodeDetailsClicked: (nodeId: number) => void;
};

// Marks the edges belonging to the selected handle, leaving every other edge untouched.
const applySelectedEdge = (edges: Edge[], selectedEdge: CustomHandle | undefined): Edge[] =>
  edges.map((edge) => {
    const handle = selectedEdge?.type === 'source' ? edge.sourceHandle : edge.targetHandle;
    const shouldBeSelected = handle === selectedEdge?.id;
    return edge.selected === shouldBeSelected ? edge : { ...edge, selected: shouldBeSelected };
  });

// The connection id is the last segment of `${node.id}.source.${connection.id}`.
const toConnectionRefs = (edges: Edge[]): ConnectionRef[] =>
  edges
    .map((edge) => ({ source: edge.source, connectionId: Number.parseInt(edge.sourceHandle?.split('.').pop() ?? '') }))
    .filter((ref) => isNumber(ref.connectionId));

const FlowMap: FC<FlowMapProps> = (props) => {
  const { metadata } = useMetadata();
  const { edgeTheme } = useThemeMode();
  const { application, setApplication: setApplication, hasUnsavedChanges } = useApplication();
  const { viewports, setViewports, centerOnNode, setCenterOnNode } = useViewportContext();
  const { handleCopy, selectedNodeIds, setSelectedNodeIds } = useClipboard();
  const { setUpdateNodes } = useUpdateNodes();
  const { selectedSubroutineId, setSelectedSubroutineId } = useSubroutine();
  const { selectedEdge, setSelectedEdge } = useSelected();
  const { confirm, showTable } = useDialogs();
  const reactFlowWrapper = useRef<HTMLDivElement>(undefined);
  const viewportsRef = useRef(viewports);
  const mousePosRef = useRef({ x: 0, y: 0 });
  const handlePasteRef = useRef<(coords: XYPosition) => void>(() => {});
  const handleCopyRef = useRef<(ids: string[]) => void>(() => {});
  const selectedNodeIdsRef = useRef(selectedNodeIds);
  const hasMouseMovedRef = useRef(false);
  const reconnectBundleRef = useRef<Edge[]>([]);

  const [openNewBlockDialog, setOpenNewBlockDialog] = useState(false);
  const [openSearchBlocksDialog, setOpenSearchBlocksDialog] = useState(false);
  const [newBlockPosition, setNewBlockPosition] = useState<XYPosition>();
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    nodes?: Node<any, string | undefined>[];
    edge?: Edge<any>;
  } | null>(null);
  const hasSelectedContextNode = contextMenu?.nodes?.some((node) => selectedNodeIds.includes(node.id));
  // User can have x nodes selected already but right click on a non-selected node.
  // This makes sure the action (copy/paste/delete) are taken place on the correct nodes.
  const nodeIdsToAction = hasSelectedContextNode ? selectedNodeIds : contextMenu?.nodes?.map((node) => node.id);
  const [showPasteOption, setShowPasteOption] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [nodes, setNodes_, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const { fitView: fitView_, setViewport, setCenter, screenToFlowPosition, getNodes } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const nodeTypes = useMemo(() => ({ customNode: NodeRenderer, customNote: CustomNote }), []);
  const connectionLineComponent = useMemo(() => createReconnectConnectionLine(reconnectBundleRef), []);
  const recentBlocks = getBlockHistory(10);
  const theme = useTheme();
  const colorShade = theme.palette.mode === 'dark' ? 900 : 200;

  const onConnect = useCallback((params: any) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  const edgeTypes = useMemo(
    () => ({
      smart: SmartEdgeCached,
      straight: StraightEdge,
      bezier: BezierEdge,
      step: StepEdge
    }),
    []
  );

  const setNodes = (nodes: Node[]) => {
    setNodes_(
      nodes.map((node) => {
        const data = {
          id: node.id,
          data: {
            ...node.data,
            onDetailsClicked: () => props.onNodeDetailsClicked(Number.parseInt(node.id))
          }
        };
        return {
          ...node,
          data: data.data,
          selected: selectedNodeIds.includes(node.id)
        };
      })
    );
  };

  const fitView = useEffectEvent((ms = 750) => {
    const runFitView = async () => {
      await sleep(ms);
      if (
        Object.keys(viewportsRef.current).length === 0 ||
        (selectedSubroutineId && !viewportsRef.current[selectedSubroutineId])
      ) {
        fitView_({ duration: 1500 });
        updateNodeInternals(getNodes().map((node) => node.id));
      }
    };

    runFitView();
  });

  const saveViewport = (viewport: Viewport | null) => {
    if (!selectedSubroutineId) return;

    if (!viewport) {
      delete viewports[selectedSubroutineId];
    } else {
      viewports[selectedSubroutineId] = viewport;
    }
    setViewports(viewports);
  };

  useOnViewportChange({
    onChange: (viewPort) => {
      saveViewport(viewPort);
    }
  });

  const syncViewport = () => {
    if (centerOnNode) return;

    const sub = viewports && selectedSubroutineId && viewports[selectedSubroutineId];
    sub ? setViewport(sub) : fitView();
  };

  const getPosition = (clipboardData: ClipboardNode[], coords: XYPosition): XYPosition => {
    const position = screenToFlowPosition(coords);

    const positions = clipboardData.map((d) => d.layout?.position).filter(isXYPosition);
    if (!positions.length) return position;

    const minX = Math.min(...positions.map((p) => p.x));
    const maxX = Math.max(...positions.map((p) => p.x));
    const minY = Math.min(...positions.map((p) => p.y));
    const maxY = Math.max(...positions.map((p) => p.y));

    return {
      x: position.x - (minX + maxX) / 2,
      y: position.y - (minY + maxY) / 2
    };
  };

  const handleNewApp = async () => {
    if (application && !hasUnsavedChanges) {
      // This should only be triggered when we load in a new App.
      setNodes([]);
      setEdges([]);
      await sleep(100);
    }
  };

  const onConnectionConnect = (connection: Connection) => {
    console.debug('onConnectionConnect:', connection);
    if (!connection || !connection.source || !connection.sourceHandle || !connection.target || !connection.targetHandle)
      return;
    const connections = application.blocks.connections[connection.source];
    // connection.sourceHandle is `${node.id}.source.${connection.id}` and we want connection.id
    const key = connection.sourceHandle?.split('.').pop();
    if (!key) return;

    const backendConnection = connections?.find((conn) => conn.id?.toString() === key);
    if (!isConnection(backendConnection)) return;

    const parsedTarget = Number.parseInt(connection.target);
    const dst = isNumber(parsedTarget) && !isNaN(parsedTarget) ? parsedTarget : undefined;
    if (!dst) return;
    backendConnection.dst = dst;
    onConnect(connection);

    setApplication(updateConnection(connection.source, backendConnection, application));
    setUpdateNodes([connection.source]);
  };

  // Clicking the input drag anchor selects the whole bundle, matching a click on the input handle.
  // Without this react-flow's own edge selection takes over and highlights only the clicked line.
  const onEdgeClick = (event: MouseEvent<Element, globalThis.MouseEvent>, edge: Edge) => {
    const clickedInputAnchor = (event.target as Element).classList?.contains('react-flow__edgeupdater-target');
    if (!clickedInputAnchor || !edge.targetHandle) return;

    setSelectedEdge({ id: edge.targetHandle, type: 'target' });
  };

  const onReconnectStart = (_event: unknown, edge: Edge) => {
    if (!isNumber(Number.parseInt(edge.target))) return;

    const bundle = edges.filter((e) => e.target === edge.target && e.targetHandle === edge.targetHandle);
    reconnectBundleRef.current = bundle;
    setIsReconnecting(true);

    const siblingIds = new Set(bundle.filter((e) => e.id !== edge.id).map((e) => e.id));
    setEdges((prev) => prev.map((e) => (siblingIds.has(e.id) ? { ...e, hidden: true } : e)));
  };

  const onReconnect = (oldEdge: Edge, newConnection: Connection) => {
    const dst = Number.parseInt(newConnection.target);
    if (!isNumber(dst) || newConnection.target === oldEdge.target) return;

    const bundle = reconnectBundleRef.current.length ? reconnectBundleRef.current : [oldEdge];
    const moves = toConnectionRefs(bundle.filter((edge) => edge.source !== newConnection.target));
    if (!moves.length) return;

    setApplication(redirectConnections(moves, dst, application));
    setUpdateNodes([...new Set([...moves.map((move) => move.source), oldEdge.target, newConnection.target])]);
  };

  const onReconnectEnd = () => {
    const bundleIds = new Set(reconnectBundleRef.current.map((e) => e.id));
    reconnectBundleRef.current = [];
    setIsReconnecting(false);
    setEdges((prev) =>
      applySelectedEdge(
        prev.map((e) => (bundleIds.has(e.id) && e.hidden ? { ...e, hidden: false } : e)),
        selectedEdge
      )
    );
  };

  const handleContextMenu = (
    event: globalThis.MouseEvent | MouseEvent<Element, globalThis.MouseEvent>,
    nodes?: Node[],
    edge?: Edge
  ) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      ...(nodes && { nodes: nodes }),
      ...(edge && { edge: edge })
    });
    if (nodes?.length && selectedNodeIds.length < 1) {
      setSelectedNodeIds(nodes.map((node) => node.id));
    }
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const handleCopyClick = () => {
    if (contextMenu?.nodes?.length && !!nodeIdsToAction) {
      handleCopy(nodeIdsToAction);
    }
    handleClose();
  };

  const handlePaste = (coords: XYPosition) => {
    getClipboardData()
      .then((clipboardData) => {
        console.debug('handlePaste.then(clipboardData)', clipboardData);
        if (!selectedSubroutineId || !isClipboardNodeData(clipboardData)) return;

        const offsetPos = getPosition(clipboardData.nodes, coords);

        const updatedClipboardData: ClipboardNode[] = clipboardData.nodes.map((data) => {
          const position = data.layout?.position;
          if (!isXYPosition(position)) return data;

          return {
            ...data,
            layout: { position: { x: position.x + offsetPos.x, y: position.y + offsetPos.y } }
          };
        });

        handlePasteNodesIntoApplication(
          application,
          setApplication,
          metadata,
          selectedSubroutineId,
          updatedClipboardData,
          setUpdateNodes,
          setSelectedNodeIds,
          confirm
        );
      })
      .finally(() => handleClose());
  };

  const handleDeleteNodeEdge = (nodes?: Node[], edges?: Edge[]) => {
    if (!selectedSubroutineId) return;

    const selectedNodes = nodes?.map((node) => node.id) ?? (contextMenu?.nodes?.length ? nodeIdsToAction : undefined);
    const selectedEdges = edges ?? (contextMenu?.edge ? [contextMenu.edge] : undefined);

    if (selectedNodes?.length) {
      let updatedApplication = structuredClone(application);
      selectedNodes.forEach((id) => {
        updatedApplication = deleteBlock(updatedApplication, selectedSubroutineId, parseInt(id));
      });
      setApplication(updatedApplication);
      setUpdateNodes(selectedNodes);
    } else if (selectedEdges?.length) {
      const edge = selectedEdges[0];
      const connectionAction = edge?.data.action as string;
      const connection = application.blocks.connections[edge.source]?.find((con) => con.action === connectionAction);
      if (connection && 'dst' in connection) {
        connection.dst = null;
        const connections = application.blocks.connections[edge.source];
        // connection.sourceHandle is `${node.id}.source.${connection.id}` and we want connection.id
        const key = edge.sourceHandle?.split('.').pop();
        if (!key) return;
        const backendConnection = connections?.find((conn) => conn.id?.toString() === key);
        if (isConnection(backendConnection)) {
          setApplication(updateConnection(edge.source, connections, application));
          setUpdateNodes(selectedEdges.map((edge) => edge.source));
        }
      }
    }
    handleClose();
  };

  const handleAddBlock = (event: MouseEvent<HTMLLIElement, globalThis.MouseEvent>, metaBlock?: Block) => {
    if (!reactFlowWrapper.current || !selectedSubroutineId) return;
    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const position = screenToFlowPosition({
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top
    });
    setNewBlockPosition(position);
    if (metaBlock) {
      // Replacing a startblock deletes the old one before the new block is added, so the id the
      // new block will receive must be computed without the current startblock.
      const currentStartblock = findSubroutine(application.subroutines, selectedSubroutineId)?.startblock;
      const nodeIds = Object.keys(application.blocks.nodes)
        .map((id) => Number.parseInt(id))
        .filter((id) => id !== currentStartblock);
      const newId = Math.max(...nodeIds, 0) + 1;
      switch (metaBlock.style) {
        case 'appstart':
          setOrReplaceAppStartblock(newId, application, metadata, selectedSubroutineId, confirm).then(
            (newApplication: Application) => {
              const addedBlock = addBlock(metadata, newApplication, selectedSubroutineId, metaBlock, position);
              setApplication(addedBlock.application);
              setUpdateNodes([addedBlock.newId.toString()]);
            }
          );
          break;

        case 'subroutine':
          setOrReplaceStartblock(newId, application, selectedSubroutineId, confirm).then(
            (newApplication: Application) => {
              const addedBlock = addBlock(metadata, newApplication, selectedSubroutineId, metaBlock, position);
              setApplication(addedBlock.application);
              setUpdateNodes([addedBlock.newId.toString()]);
            }
          );
          break;

        default: {
          const addedBlock = addBlock(metadata, application, selectedSubroutineId, metaBlock, position);
          setApplication(addedBlock.application);
          setUpdateNodes([addedBlock.newId.toString()]);
          break;
        }
      }
    } else {
      setOpenNewBlockDialog(true);
    }
    handleClose();
  };

  const handleAddNote = (event: MouseEvent<HTMLLIElement, globalThis.MouseEvent>) => {
    event.preventDefault();
    const noteMetaBlock = metadata.blocks.find((block) => block.style === 'note');
    if (!reactFlowWrapper.current || !noteMetaBlock || !selectedSubroutineId) return;
    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const position = screenToFlowPosition({
      x: event.clientX - reactFlowBounds.left,
      y: event.clientY - reactFlowBounds.top
    });
    setApplication(addBlock(metadata, application, selectedSubroutineId, noteMetaBlock, position).application);
    handleClose();
  };

  const handleSearch = (blockId: string | undefined) => {
    if (!blockId) return;
    setCenterOnNode(blockId);
  };

  const handleChangeSubroutine = (newSubroutineId: number) => {
    setSelectedSubroutineId(newSubroutineId);
    handleClose();
  };

  const centerViewportOnNode = (nodeId: number | string) => {
    const position = application.blocks.layouts[nodeId]?.position;
    if (isXYPosition(position)) {
      setCenter(position.x + 170, position.y + 128 / 2, { duration: 1500 });
    }
    setCenterOnNode();
  };

  handlePasteRef.current = handlePaste;
  handleCopyRef.current = handleCopy;
  selectedNodeIdsRef.current = selectedNodeIds;

  useEffect(() => {
    const newApplication = migrateApplication({ ...application }, metadata, showTable);
    setApplication(newApplication, true);
    setUpdateNodes(['*']);
  }, []);

  useEffect(() => {
    if (!isNumber(selectedSubroutineId)) return;
    viewportsRef.current = viewports;
    centerOnNode ? centerViewportOnNode(centerOnNode) : syncViewport();
  }, [selectedSubroutineId, viewports]);

  useEffect(() => {
    if (centerOnNode === undefined) return;
    application.subroutines.forEach((subroutine) => {
      const foundNode = subroutine.nodes?.find((id) => id.toString() == centerOnNode);
      if (isNumber(foundNode)) {
        subroutine.id !== selectedSubroutineId
          ? setSelectedSubroutineId(subroutine.id)
          : centerViewportOnNode(foundNode);
      }
    });
  }, [centerOnNode]);

  useEffect(() => {
    if (contextMenu !== null) {
      hasClipboardData('nodes')
        .then((isClipboardData) => {
          setShowPasteOption(isClipboardData);
        })
        .catch(() => setShowPasteOption(false));
    } else {
      setShowPasteOption(false);
    }
  }, [contextMenu]);

  useEffect(() => {
    setEdges((prev) => applySelectedEdge(prev, selectedEdge));
  }, [selectedEdge]);

  useEffect(() => {
    console.debug('useEffect', 'application, metadata, selectedSubroutineId');
    if (application && metadata && selectedSubroutineId) {
      blockAdapter().toFlow(
        application,
        selectedSubroutineId,
        selectedNodeIds,
        edgeTheme,
        async (newNodes, newEdges) => {
          await handleNewApp();
          console.debug('flow modified!', newNodes, newEdges);
          setNodes(newNodes);
          setEdges(newEdges);
          updateNodeInternals(newNodes.map((n) => n.id));
        }
      );
    }
  }, [application.blocks.connections, edgeTheme, metadata, selectedSubroutineId]);

  useEffect(() => {
    const el = reactFlowWrapper.current;
    if (!el) return;

    const onMouseMove = (e: globalThis.MouseEvent) => {
      mousePosRef.current = { x: e.clientX + 2, y: e.clientY - 6 };
      hasMouseMovedRef.current = true;
    };

    const onCopy = () => {
      handleCopyRef.current(selectedNodeIdsRef.current);
    };

    const onPaste = () => {
      if (!hasMouseMovedRef.current || !el.matches(':hover')) return;
      handlePasteRef.current(mousePosRef.current);
      hasMouseMovedRef.current = false;
    };

    el.addEventListener('copy', onCopy);
    el.addEventListener('mousemove', onMouseMove);
    document.addEventListener('paste', onPaste);

    return () => {
      el.removeEventListener('copy', onCopy);
      el.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('paste', onPaste);
    };
  }, []);

  return (
    <Box width='100%' height='inherit' flexGrow={0} position='relative' ref={reactFlowWrapper}>
      {nodes && edges && (
        <ReactFlow
          className={isReconnecting ? 'reconnecting' : undefined}
          nodes={nodes}
          edges={edges}
          edgeTypes={edgeTypes}
          onEdgesChange={onEdgesChange}
          onNodesChange={onNodesChange}
          onNodeDragStop={(_, __, nodes) => {
            setApplication(
              updatePositions(
                nodes.map((node) => node.id),
                nodes.map((node) => node.position),
                application
              )
            );
          }}
          onConnect={onConnectionConnect}
          onEdgeClick={onEdgeClick}
          onReconnect={onReconnect}
          onReconnectStart={onReconnectStart}
          onReconnectEnd={onReconnectEnd}
          reconnectRadius={14}
          nodeTypes={nodeTypes}
          onInit={syncViewport}
          minZoom={0.1}
          snapToGrid
          snapGrid={[10, 10]}
          onlyRenderVisibleElements
          connectionLineType={ConnectionLineType.SmoothStep}
          connectionLineComponent={connectionLineComponent}
          multiSelectionKeyCode={['Meta', 'Control']}
          deleteKeyCode={['Delete', 'Backspace']}
          proOptions={{ hideAttribution: true }}
          onNodeContextMenu={(event, node) => handleContextMenu(event, [node])}
          onEdgeContextMenu={(event, edge) => handleContextMenu(event, undefined, edge)}
          onPaneContextMenu={(event) => handleContextMenu(event)}
          onSelectionContextMenu={(event, nodes) => handleContextMenu(event, nodes)}
          onSelectionChange={({ nodes }) => setSelectedNodeIds(nodes.map((node) => node.id))}
          onDelete={({ nodes, edges }) => (nodes.length || edges.length) && handleDeleteNodeEdge(nodes, edges)}
        >
          <Controls />
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        </ReactFlow>
      )}
      <SearchBlocksButton onClick={() => setOpenSearchBlocksDialog(true)} />
      <AddBlockButton onClick={() => setOpenNewBlockDialog(true)} />
      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference='anchorPosition'
        anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        slotProps={dismissOnContextMenu(handleClose)}
      >
        {showPasteOption && (
          <MenuItem onClick={(event) => handlePaste({ x: event.clientX, y: event.clientY })}>
            <ListItemIcon>
              <ContentPasteGoIcon />
            </ListItemIcon>
            Paste
          </MenuItem>
        )}
        {showPasteOption && <Divider />}
        {!!contextMenu?.nodes?.length && (
          <MenuItem onClick={handleCopyClick}>
            <ListItemIcon>
              <ContentCopyIcon />
            </ListItemIcon>
            {`Copy block${hasSelectedContextNode && selectedNodeIds.length > 1 ? 's' : ''}`}
          </MenuItem>
        )}
        {!!contextMenu?.nodes?.length || !!contextMenu?.edge ? (
          <ConfirmPopover
            component='MenuItem'
            onConfirm={handleDeleteNodeEdge}
            color={theme.palette.error.main}
            icon={<ClearIcon />}
            buttonText={`Delete ${
              (contextMenu?.nodes && `block${hasSelectedContextNode && selectedNodeIds.length > 1 ? 's' : ''}`) ||
              (contextMenu?.edge && 'connection') ||
              ''
            }`}
            text={`Remove ${
              (contextMenu?.nodes?.length &&
                `block${hasSelectedContextNode && selectedNodeIds.length > 1 ? 's' : ''}`) ||
              (contextMenu?.edge && 'connection') ||
              ''
            }?`}
          />
        ) : null}

        {contextMenu?.nodes &&
          metadata.blocks.find(
            (block) => contextMenu.nodes?.length === 1 && contextMenu.nodes[0].data.backendNode.type === block.type
          )?.style === 'reference' && (
            <MenuItem
              disabled={
                !contextMenu.nodes[0].data.details.substart ||
                selectedSubroutineId === contextMenu.nodes[0].data.details.substart
              }
              onClick={() => handleChangeSubroutine(contextMenu.nodes && contextMenu.nodes[0].data.details.substart)}
            >
              <ListItemIcon>
                <StopTwoToneIcon />
              </ListItemIcon>
              Go to subroutine
            </MenuItem>
          )}
        {!!contextMenu?.nodes || !!contextMenu?.edge ? <Divider /> : null}
        <MenuItem onClick={handleAddNote}>
          <ListItemIcon>
            <StickyNote2Icon />
          </ListItemIcon>
          New note
        </MenuItem>
        <MenuItem onClick={handleAddBlock}>
          <ListItemIcon>
            <AddIcon />
          </ListItemIcon>
          New block...
        </MenuItem>
        {application && !!recentBlocks.length && <Divider />}
        {application &&
          !!recentBlocks.length &&
          recentBlocks.map((block, index) => {
            if (!block) return <></>;
            return (
              <MenuItem key={`recents.${block.type}.${index}`} onClick={(e) => handleAddBlock(e, block)}>
                <ListItemIcon>
                  <AddBoxIcon
                    sx={{
                      color: getColorFromString(block.style, colorShade)
                    }}
                  />
                </ListItemIcon>
                {block.type}
              </MenuItem>
            );
          })}
      </Menu>
      <NewBlockDialog
        open={openNewBlockDialog}
        onClose={() => {
          setNewBlockPosition(undefined);
          setOpenNewBlockDialog(false);
        }}
        position={newBlockPosition}
      />
      <SearchBlocksDialog
        open={openSearchBlocksDialog}
        onClose={() => {
          setNewBlockPosition(undefined);
          setOpenSearchBlocksDialog(false);
        }}
        search={(blockId) => handleSearch(blockId)}
      />
    </Box>
  );
};

export default FlowMap;
